/**
 * InvoicePaymentModal — Partial payment UX (Apr 26, 2026)
 *
 * Shows all line items of an invoice as checkable rows so accounting can
 * mark a SUBSET of files as paid in a single payment event. The selection
 * defaults to "all currently unpaid items" (most common case = client paid
 * the full balance), but every row can be unchecked individually.
 *
 * Captures one shared payment_reference / payment_date / notes per event
 * (per accounting feedback — one client payment usually covers N files).
 *
 * Submitting calls `POST /api/pricing/billing/invoice/{id}/mark-orders-paid`
 * which:
 *   • marks the selected orders billing_status=paid + payment metadata
 *   • recomputes invoice.status (paid / partially_paid / invoiced)
 *   • writes a billing_audit_log entry with old/new statuses + actor
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { CreditCard, CheckCircle2, X } from 'lucide-react';
import { formatDateOnly } from '../../../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return formatDateOnly(iso);
  } catch {
    return '';
  }
};

const todayISO = () => new Date().toISOString().slice(0, 10);

export const InvoicePaymentModal = ({ open, onClose, invoice, onPaymentApplied }) => {
  const lineItems = useMemo(
    () => (invoice?.line_items || []).map((li) => ({ ...li })),
    [invoice]
  );
  
  // Pre-check all currently unpaid line items
  const initialSelected = useMemo(() => {
    const set = new Set();
    lineItems.forEach((li) => {
      if (!li.paid && li.order_id) set.add(li.order_id);
    });
    return set;
  }, [lineItems]);
  
  const [selected, setSelected] = useState(initialSelected);
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentDate, setPaymentDate] = useState(todayISO());
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Reset state every time the modal opens for a different invoice
  useEffect(() => {
    if (open) {
      setSelected(initialSelected);
      setPaymentRef('');
      setPaymentDate(todayISO());
      setNotes('');
    }
  }, [open, initialSelected]);
  
  const unpaidItems = useMemo(
    () => lineItems.filter((li) => !li.paid),
    [lineItems]
  );
  const paidItems = useMemo(
    () => lineItems.filter((li) => li.paid),
    [lineItems]
  );
  
  const selectedTotal = useMemo(
    () =>
      lineItems
        .filter((li) => selected.has(li.order_id))
        .reduce((sum, li) => sum + (li.total ?? li.unit_price ?? 0), 0),
    [lineItems, selected]
  );
  const paidAlreadyTotal = useMemo(
    () => paidItems.reduce((s, li) => s + (li.total ?? li.unit_price ?? 0), 0),
    [paidItems]
  );
  const invoiceTotal = invoice?.total_amount || 0;
  const remainingAfter = Math.max(0, invoiceTotal - paidAlreadyTotal - selectedTotal);
  
  const toggleOrder = (orderId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };
  
  const toggleAll = () => {
    const allIds = unpaidItems.map((li) => li.order_id).filter(Boolean);
    if (allIds.every((id) => selected.has(id))) {
      // Deselect all unpaid
      setSelected((prev) => {
        const next = new Set(prev);
        allIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        allIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };
  
  const handleSubmit = async () => {
    if (selected.size === 0) {
      toast.error('Select at least one file to mark as paid');
      return;
    }
    if (!invoice?.id) return;
    
    setSubmitting(true);
    try {
      const resp = await axios.post(
        `${API}/pricing/billing/invoice/${invoice.id}/mark-orders-paid`,
        {
          order_ids: Array.from(selected),
          payment_reference: paymentRef || null,
          payment_date: paymentDate ? new Date(paymentDate).toISOString() : null,
          notes: notes || null,
        }
      );
      
      const newStatus = resp.data?.invoice_status;
      const paidCount = resp.data?.orders_paid?.length || 0;
      
      if (newStatus === 'paid') {
        toast.success(`Invoice fully paid — ${paidCount} file(s) marked paid`);
      } else if (newStatus === 'partially_paid') {
        toast.success(
          `${paidCount} file(s) marked paid · invoice now partially paid`
        );
      } else {
        toast.success(`${paidCount} file(s) marked as paid`);
      }
      
      onPaymentApplied && onPaymentApplied(resp.data);
      onClose && onClose();
    } catch (err) {
      console.error('Partial payment failed:', err);
      toast.error(
        err.response?.data?.detail || 'Failed to record payment'
      );
    } finally {
      setSubmitting(false);
    }
  };
  
  if (!invoice) return null;
  
  const allUnpaidSelected =
    unpaidItems.length > 0 &&
    unpaidItems.every((li) => selected.has(li.order_id));
  
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent
        className="max-w-3xl p-0 gap-0 overflow-hidden"
        data-testid="invoice-payment-modal"
      >
        <DialogHeader className="px-6 py-4 border-b border-slate-100">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold text-slate-900">
            <CreditCard className="w-4 h-4 text-emerald-600" />
            Record Payment — {invoice.invoice_number}
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            {invoice.client_name || invoice.client_code} · {lineItems.length} file(s) · {formatCurrency(invoiceTotal)}
          </p>
        </DialogHeader>
        
        {/* Line items */}
        <div className="max-h-[50vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="tf-thead sticky top-0 z-10">
              <tr>
                <th className="px-4 py-2.5 w-10">
                  <input
                    type="checkbox"
                    checked={allUnpaidSelected}
                    onChange={toggleAll}
                    disabled={unpaidItems.length === 0}
                    className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="payment-select-all"
                  />
                </th>
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-[#4338CA] uppercase tracking-wide">
                  Order #
                </th>
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-[#4338CA] uppercase tracking-wide">
                  Product ID
                </th>
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-[#4338CA] uppercase tracking-wide">
                  Property
                </th>
                <th className="px-3 py-2.5 text-right text-[11px] font-semibold text-[#4338CA] uppercase tracking-wide">
                  Amount
                </th>
                <th className="px-3 py-2.5 text-center text-[11px] font-semibold text-[#4338CA] uppercase tracking-wide w-24">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((li) => {
                const orderId = li.order_id;
                const isPaid = !!li.paid;
                const isSelected = selected.has(orderId);
                const amount = li.total ?? li.unit_price ?? 0;
                
                return (
                  <tr
                    key={orderId || li.order_number}
                    className={`border-b border-slate-100 last:border-0 transition-colors ${
                      isPaid
                        ? 'bg-emerald-50/30'
                        : isSelected
                          ? 'bg-indigo-50/40'
                          : 'hover:bg-slate-50'
                    }`}
                    data-testid={`payment-row-${li.order_number}`}
                  >
                    <td className="px-4 py-3 align-middle">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => orderId && toggleOrder(orderId)}
                        disabled={isPaid || !orderId}
                        className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                        data-testid={`payment-row-checkbox-${li.order_number}`}
                      />
                    </td>
                    <td className="px-3 py-3 font-mono text-xs text-slate-700">
                      {li.order_number || '—'}
                    </td>
                    <td className="px-3 py-3 font-medium text-slate-900 tabular-nums">
                      {li.product_id || li.order_number || '—'}
                    </td>
                    <td className="px-3 py-3 text-slate-600 truncate max-w-[260px]">
                      {li.property_address || '—'}
                    </td>
                    <td className="px-3 py-3 text-right font-semibold tabular-nums text-slate-900">
                      {formatCurrency(amount)}
                    </td>
                    <td className="px-3 py-3 text-center">
                      {isPaid ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                          <CheckCircle2 className="w-3 h-3" />
                          Paid {li.paid_at ? `· ${formatDate(li.paid_at)}` : ''}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-200">
                          Unpaid
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {lineItems.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-sm text-slate-400">
                    No line items on this invoice
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Payment metadata + totals */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/40 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                Payment Reference
              </label>
              <Input
                type="text"
                placeholder="Check # / Wire ID"
                value={paymentRef}
                onChange={(e) => setPaymentRef(e.target.value)}
                className="h-9 text-sm"
                data-testid="payment-reference-input"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                Payment Date
              </label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="h-9 text-sm"
                data-testid="payment-date-input"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1">
                Notes (optional)
              </label>
              <Input
                type="text"
                placeholder="Internal note"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="h-9 text-sm"
                data-testid="payment-notes-input"
              />
            </div>
          </div>
          
          {/* Totals strip */}
          <div className="flex items-center justify-between gap-4 px-3 py-2 rounded-md bg-white border border-slate-200">
            <div className="flex items-center gap-4 text-xs text-slate-500">
              <span>
                Invoice: <span className="font-semibold text-slate-800 tabular-nums">{formatCurrency(invoiceTotal)}</span>
              </span>
              {paidItems.length > 0 && (
                <span>
                  Already paid: <span className="font-semibold text-emerald-600 tabular-nums">{formatCurrency(paidAlreadyTotal)}</span>
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 text-xs">
              <span className="text-slate-500">
                Selected: <span className="font-semibold text-indigo-600 tabular-nums">{formatCurrency(selectedTotal)}</span> · {selected.size} file(s)
              </span>
              <span className="text-slate-500">
                Remaining: <span className={`font-semibold tabular-nums ${remainingAfter > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>{formatCurrency(remainingAfter)}</span>
              </span>
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            className="h-9"
            data-testid="payment-cancel-btn"
          >
            <X className="w-4 h-4 mr-1.5" />
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || selected.size === 0}
            className="h-9 px-4 bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-slate-300"
            data-testid="payment-submit-btn"
          >
            <CheckCircle2 className="w-4 h-4 mr-1.5" />
            {submitting
              ? 'Recording…'
              : selected.size > 0
                ? `Record Payment (${selected.size})`
                : 'Record Payment'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InvoicePaymentModal;
